import { listPricedClaimTemplateVolumes } from '../../capabilities/container-storage/utils';
import { NIC_RESOURCE } from '../../components/nicPricing/utils';
import { buildDiskPricingWatchFields, buildVendorDeviceMetaWatchFields } from '../../components/vendorDevice';
import type { PipelineContext } from '../../core/types';
import type { ResourceConfigEntry, ResourceType, SkuModuleMeta } from '../../platform/pipeline-kernel/types';
import { resolvePricingRequirement } from '../../utils/postPaidPricing';
import { pickPricingResourceMeta } from '../../utils/pricingResourceMeta';

/** 训练集群共享存储卷计价资源。 */
export const TC_DATA_VOLUME_RESOURCE: ResourceType = {
  requirement: '',
  code: 'DataVolume',
  description: '共享存储卷',
  unit: 'Gi',
};

/** 训练集群计价监听配置。 */
/** 节点组 JSON / 拓扑副本变更须触发算价（isResourceChange） */
const TC_NODE_REPLICA_WATCH_FIELDS = ['masterNodes', 'workerNodes', 'runMode', 'replicas'] as const;

export const TC_RESOURCE_CONFIGS: ResourceConfigEntry[] = [
  {
    requirement: '',
    code: 'vCPU',
    description: 'CPU',
    unit: 'Core',
    watchFields: [...TC_NODE_REPLICA_WATCH_FIELDS],
    displayIds: ['compute-cpu-total'],
  },
  {
    requirement: '',
    code: 'Memory',
    description: 'Memory',
    unit: 'Gi',
    watchFields: [...TC_NODE_REPLICA_WATCH_FIELDS],
    displayIds: ['compute-memory-total'],
  },
  {
    requirement: '',
    code: 'vGPU',
    description: 'GPU',
    unit: 'Card',
    watchFields: [...TC_NODE_REPLICA_WATCH_FIELDS],
    displayIds: ['compute-gpu-total'],
  },
  {
    requirement: '',
    code: 'SystemVolume',
    description: 'System Volume',
    unit: 'Gi',
    watchFields: [
      'master.systemDiskSize',
      'worker.systemDiskSize',
      ...buildVendorDeviceMetaWatchFields('masterSystemDiskPricing'),
      ...buildVendorDeviceMetaWatchFields('workerSystemDiskPricing'),
      ...TC_NODE_REPLICA_WATCH_FIELDS,
    ],
    displayIds: ['system-volume'],
  },
  {
    ...TC_DATA_VOLUME_RESOURCE,
    watchFields: ['trainingSharedVolumes', ...buildDiskPricingWatchFields('trainingSharedVolumes.0')],
    displayIds: ['training-shared-storage-total'],
  },
  {
    ...NIC_RESOURCE,
    // 下单 NIC 数量会乘 Head/Worker 总副本，节点/拓扑副本变更需重算
    watchFields: [...TC_NODE_REPLICA_WATCH_FIELDS, 'network.nicPricing'],
    displayIds: ['training_network_segments'],
  },
  {
    requirement: '',
    code: 'SkuModule',
    description: 'SKU Module Selection',
    pricing: false,
    watchFields: ['skuResourceModules'],
  },
  {
    requirement: '',
    code: 'DisplayMeta',
    description: 'Display Meta',
    pricing: false,
    watchFields: ['runtime.type', 'masterNodes', 'workerNodes', 'commonEnv', 'initCommand'],
    displayIds: ['inference_framework'],
  },
];

/** 训练集群模块的资源锁定与预置判断。 */
export const TC_SKU_MODULE_META: Record<string, SkuModuleMeta> = {
  envVars: { resource: false, presetCheckFields: ['commonEnv'], orderListItemLock: true },
  nodeGroups: { resource: true, presetCheckFields: ['masterNodes', 'workerNodes'] },
  storage: {
    resource: true,
    presetCheckFields: ['trainingSharedVolumes'],
    quantityFields: ['containerStorage.list'],
    quantityPresetPaths: { 'containerStorage.list': ['trainingSharedVolumes'] },
  },
  initCommand: { resource: false, presetCheckFields: ['initCommand'] },
};

/** 从共享存储卷表单生成计价资源。 */
export function buildTrainingSharedVolumeResourceConfigs(rawVolumes: unknown, ctx: PipelineContext): ResourceType[] {
  const result: ResourceType[] = [];
  let index = 0;
  listPricedClaimTemplateVolumes(Array.isArray(rawVolumes) ? rawVolumes : [], { paymentTiming: ctx.paymentTiming }).forEach((item) => {
    const requirement = resolvePricingRequirement(item.volumeCapacity, {
      paymentTiming: ctx.paymentTiming,
      skuType: ctx.sku?.skuType,
      vendorMeta: item.pricing,
      purpose: ctx.resourceQuantityPurpose,
    });
    if (requirement == null) return;
    result.push({
      ...TC_DATA_VOLUME_RESOURCE,
      description: index > 0 ? `${TC_DATA_VOLUME_RESOURCE.description}${index + 1}` : TC_DATA_VOLUME_RESOURCE.description,
      ...pickPricingResourceMeta(item.pricing, requirement),
      requirement,
    });
    index += 1;
  });
  return result;
}
